/**
 * Public policy page paths (task 422).
 *
 * `/privacy` and `/terms` are the ONLY fully public pages in the app: they are
 * linked from the Keycloak registration screen (register.ftl, task 421) and
 * from the ConsentGate interstitial, so they must be reachable by visitors who
 * are not authenticated and by authenticated users whose token still carries
 * `consent_pending=true`.
 *
 * App.tsx checks `isPublicPolicyPath(window.location.pathname)` BEFORE mounting
 * the auth stack (AuthProvider → AuthGate → ConsentGate → Router), so these
 * pages render outside every auth/consent/entitlement gate. Keep this list in
 * sync with the pages App.tsx maps them to.
 */

export const PRIVACY_PATH = '/privacy';
export const TERMS_PATH = '/terms';

export const PUBLIC_POLICY_PATHS: readonly string[] = [PRIVACY_PATH, TERMS_PATH];

/**
 * True when `pathname` addresses one of the public policy pages.
 * Tolerates a single trailing slash (e.g. `/privacy/`).
 */
export const isPublicPolicyPath = (pathname: string): boolean =>
  PUBLIC_POLICY_PATHS.includes(normalizePath(pathname));

/**
 * The invitation landing route (task 731).
 *
 * Deliberately NOT a member of `PUBLIC_POLICY_PATHS`, because its semantics are
 * different in a way that matters: the policy pages render entirely OUTSIDE the
 * auth stack and never need a token. `/accept-invite` is **auth-optional** — it
 * must render for a visitor who has no login yet (to tell them who invited
 * them and steer them into registration), and then render again for the same
 * person once they come back signed in, when it needs a token to complete
 * acceptance.
 *
 * So the auth stack still mounts for this path; what changes is that
 * `AuthProvider` does not force a Keycloak redirect on it, and `AuthGate`
 * renders the landing page instead of blocking. Every other route is untouched:
 * unauthenticated visitors are still bounced to Keycloak exactly as before.
 */
export const ACCEPT_INVITE_PATH = '/accept-invite';

/**
 * True when `pathname` addresses a route that renders inside the auth stack but
 * must not trigger the login redirect when the visitor is anonymous.
 */
export const isAuthOptionalPath = (pathname: string): boolean =>
  normalizePath(pathname) === ACCEPT_INVITE_PATH;

/**
 * The registration entry path (task 792).
 *
 * Marketing CTAs on the website need a target that puts a brand-new visitor on
 * Keycloak's **registration** form rather than its sign-in form. They cannot
 * link at Keycloak directly: the `innovate-app` client requires PKCE, and
 * `/protocol/openid-connect/registrations` without a `code_challenge` is
 * rejected with `invalid_request` (verified on Keycloak 26.6.2, task 731). Only
 * keycloak-js can mint the PKCE pair and `state`, so the app has to be what
 * starts the flow — hence an entry path here instead of a static URL.
 *
 * Deliberately NOT a member of the auth-optional list, even though both are
 * reachable without a login: `/accept-invite` must RENDER while anonymous,
 * whereas `/register` must REDIRECT while anonymous. Treating it as
 * auth-optional would strand the visitor on a route with nothing on it.
 *
 * Optional `?login_hint=<email>` on the URL is forwarded to Keycloak as
 * `loginHint`, which prefills the registration form's email field (task 731
 * verified the prefill live). Nothing else is read off the query string.
 */
export const REGISTER_PATH = '/register';

/**
 * True when `pathname` addresses the registration entry path — the one route
 * where an anonymous visitor is sent to Keycloak's registration form instead of
 * its sign-in form.
 */
export const isRegisterPath = (pathname: string): boolean =>
  normalizePath(pathname) === REGISTER_PATH;

/** Drop a single trailing slash (e.g. `/privacy/` → `/privacy`). */
function normalizePath(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
}
